import { Controller } from '@hotwired/stimulus';

type Summary = {
  vehicle: { vin: string; name: string | null; year: number | null; make: string | null; model: string | null; trim: string | null; engine: string | null };
  listing_available?: boolean;
  listing: null | { active: boolean; price: number | null; mileage: number | null; seller_name: string | null; city: string | null; state: string | null; first_seen: string | null; last_seen: string | null };
  market: null | { low: number; median: number; high: number; comparable_count: number; verdict: string | null; headline: string | null };
  history: { listings_count: number; first_seen: string | null; price_change: number | null };
  auctions: { available: boolean; count: number | null; risk_score: number | null; risk_band: string; title_status: string | null };
  recalls: null | { count: number; scope: 'model' };
  window_sticker: boolean;
  links: { vdp: string; vehileaks: string };
};

// Same wording as Reports::AuctionRiskScoreQuery::BANDS labels.
const BAND_LABELS: Record<string, string> = { low: 'low risk', moderate: 'moderate risk', elevated: 'elevated risk', high: 'high risk', avoid: 'avoid' };
const money = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
const monthYear = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const titleCase = (s: string) => s.split(/\s+/).map(w => /^[A-Z0-9-]{2,3}$/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

export default class extends Controller<HTMLElement> {
  static targets = ['form', 'input', 'submit', 'result', 'error', 'title', 'listing', 'market', 'history',
    'auctions', 'recalls', 'sticker', 'gauge', 'noAuctions', 'vdpLink', 'vlLink', 'preview'];
  static values = { endpoint: String };

  declare readonly formTarget: HTMLFormElement;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly resultTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly titleTarget: HTMLElement;
  declare readonly listingTarget: HTMLElement;
  declare readonly marketTarget: HTMLElement;
  declare readonly historyTarget: HTMLElement;
  declare readonly auctionsTarget: HTMLElement;
  declare readonly recallsTarget: HTMLElement;
  declare readonly stickerTarget: HTMLElement;
  declare readonly gaugeTarget: HTMLElement;
  declare readonly noAuctionsTarget: HTMLElement;
  declare readonly vdpLinkTarget: HTMLAnchorElement;
  declare readonly vlLinkTarget: HTMLAnchorElement;
  declare readonly previewTarget: HTMLElement;
  declare readonly hasPreviewTarget: boolean;
  declare readonly endpointValue: string;

  // data-ready tells tests (and CSS, if ever needed) the JS path is live; before
  // it, a submit falls back to the native GET /vin-check → /vehicle/<VIN>.
  connect() { this.formTarget.addEventListener('submit', this.onSubmit); this.element.dataset.ready = ''; }
  disconnect() { this.formTarget.removeEventListener('submit', this.onSubmit); delete this.element.dataset.ready; }

  onSubmit = async (e: Event) => {
    e.preventDefault();
    const vin = this.inputTarget.value.trim().toUpperCase();
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) { this.showError('Please enter a valid 17-character VIN (no I, O or Q).'); return; }
    this.submitTarget.disabled = true;
    this.hideError();
    this.resultTarget.hidden = true;
    try {
      const res = await fetch(`${this.endpointValue}${vin}`, { headers: { Accept: 'application/json' } });
      // 429 can be this visitor's 30/h limit OR the site-wide cap — say neither.
      if (res.status === 429) { this.showError('Free VIN checks are busy right now. Please try again in a little while.'); return; }
      if (res.status === 400) { this.showError('This VIN doesn\'t pass the check-digit test — double-check the 17 characters.'); return; }
      if (res.status === 404) { this.showError('We couldn\'t find this VIN. Double-check the 17 characters, or try again later.'); return; }
      if (!res.ok) { this.showError('Something went wrong. Please try again in a moment.'); return; }
      this.render((await res.json()) as Summary);
    } catch {
      this.showError('Network error. Please try again.');
    } finally {
      this.submitTarget.disabled = false;
    }
  };

  render(d: Summary) {
    const v = d.vehicle;
    this.titleTarget.textContent = v.name || [v.year, v.make && titleCase(v.make), v.model && titleCase(v.model), v.trim].filter(Boolean).join(' ');

    const l = d.listing;
    if (l) {
      // Active = still on a dealer site (sites_mask > 0), even without a price.
      const where = [l.seller_name, [l.city, l.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
      const rest: string[] = [];
      if (l.mileage) rest.push(`${l.mileage.toLocaleString('en-US')} mi`);
      if (!l.active && monthYear(l.last_seen)) rest.push(monthYear(l.last_seen));
      this.setFact(this.listingTarget, l.active ? 'Listed at' : 'Last listed', [
        l.price ? this.strong(money(l.price)) : (l.active ? 'Price not shown' : 'Price not recorded'),
        rest.length ? ' · ' + rest.join(' · ') : '',
      ], l.active && where ? where : null);
    } else {
      this.setFact(this.listingTarget, 'Listed at', [d.listing_available === false ? 'Listing data unavailable right now — try again in a few minutes.' : 'We haven\'t seen this car listed for sale.']);
    }

    const m = d.market;
    if (m) {
      const badge = m.headline ? this.badge(m.headline, m.verdict?.startsWith('below') ? 'good' : m.verdict?.startsWith('above') ? 'warn' : 'neutral') : '';
      this.setFact(this.marketTarget, 'Typical price', [this.strong(`${money(m.low)}–${money(m.high)}`), ` · median ${money(m.median)} `, badge]);
    } else this.hideFact(this.marketTarget);

    const h = d.history;
    if (h.listings_count > 0) {
      this.setFact(this.historyTarget, 'History', [
        `Seen in ${h.listings_count} ${h.listings_count === 1 ? 'listing' : 'listings'}${h.first_seen ? ' since ' + monthYear(h.first_seen) : ''}`,
        h.price_change ? ` · price ${h.price_change < 0 ? '−' : '+'}${money(Math.abs(h.price_change))}` : '',
      ]);
    } else this.hideFact(this.historyTarget);

    // A failed auction lookup says so — never "No auction records found".
    const a = d.auctions;
    const seen = a.available && (a.count ?? 0) > 0;
    this.noAuctionsTarget.textContent = a.available ? '✓ No auction records found' : 'Auction records unavailable right now — try again in a few minutes';
    this.noAuctionsTarget.classList.toggle('mkt-vr-check__ok', a.available);
    this.noAuctionsTarget.hidden = seen;
    this.gaugeTarget.hidden = !seen;
    if (seen) this.renderGauge(a);

    if (d.recalls && d.recalls.count > 0) {
      this.setFact(this.recallsTarget, 'Recalls', [`${d.recalls.count} NHTSA ${d.recalls.count === 1 ? 'recall' : 'recalls'} for this model`]);
    } else this.hideFact(this.recallsTarget);
    this.stickerTarget.hidden = !d.window_sticker;

    this.vdpLinkTarget.href = d.links.vdp;
    this.vdpLinkTarget.hidden = !d.listing;
    this.vlLinkTarget.href = d.links.vehileaks;
    // The "what the free check shows" list has done its job once real facts
    // are on screen; errors leave it visible.
    if (this.hasPreviewTarget) this.previewTarget.hidden = true;
    this.resultTarget.hidden = false;
  }

  renderGauge(a: Summary['auctions']) {
    this.auctionsTarget.replaceChildren(
      this.strong(`Seen at auction ${a.count}×`),
      ` · ${BAND_LABELS[a.risk_band] || a.risk_band} (${a.risk_score ?? '—'}/100)`,
      a.title_status === 'branded' ? this.badge('Branded title', 'warn') : '',
    );
    const gauge = this.gaugeTarget.querySelector('.mkt-vr-gauge') as HTMLElement | null;
    if (!gauge) return;
    gauge.classList.remove(...Array.from(gauge.classList).filter(c => c.startsWith('mkt-vr-gauge--') && !/--(sm|md|lg)$/.test(c)));
    gauge.classList.add(`mkt-vr-gauge--${a.risk_band}`);
    const num = gauge.querySelector('.mkt-vr-gauge__num');
    if (num) num.textContent = a.risk_score == null ? '—' : String(a.risk_score);
    const band = gauge.querySelector('.mkt-vr-gauge__band');
    if (band) band.textContent = a.risk_band.toUpperCase();
    const bar = gauge.querySelector<SVGCircleElement>('.mkt-vr-gauge__bar');
    if (bar) {
      const c = parseFloat(bar.getAttribute('stroke-dasharray') || '0');
      bar.setAttribute('stroke-dashoffset', String(c * (1 - (a.risk_score ?? 0) / 100)));
    }
    gauge.setAttribute('aria-label', `Auction risk score ${a.risk_score} of 100, ${a.risk_band}`);
  }

  // Builds "<dt>label</dt><dd>value…<span sub/></dd>" with DOM nodes only —
  // seller names and cities come from scraped data, never innerHTML.
  setFact(el: HTMLElement, label: string, value: (Node | string)[], sub: string | null = null) {
    const dt = document.createElement('dt');
    dt.className = 'mkt-vr-check__k';
    dt.textContent = label;
    const dd = document.createElement('dd');
    dd.className = 'mkt-vr-check__v';
    dd.append(...value);
    if (sub) {
      const s = document.createElement('span');
      s.className = 'mkt-vr-check__sub';
      s.textContent = sub;
      dd.append(s);
    }
    el.replaceChildren(dt, dd);
    el.hidden = false;
  }

  hideFact(el: HTMLElement) { el.hidden = true; el.replaceChildren(); }

  strong(text: string) { const b = document.createElement('strong'); b.textContent = text; return b; }

  // Deal verdict / title brand as a coloured pill: below market = good for a
  // buyer (green), fair = neutral blue, above market or branded = amber.
  badge(text: string, tone: 'good' | 'warn' | 'neutral') {
    const b = document.createElement('span');
    b.className = `mkt-vr-check__badge mkt-vr-check__badge--${tone}`;
    b.textContent = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    return b;
  }

  showError(msg: string) { this.errorTarget.textContent = msg; this.errorTarget.hidden = false; }
  hideError() { this.errorTarget.hidden = true; this.errorTarget.textContent = ''; }
}
